import React, { useRef, useEffect, useCallback } from 'react'
import css from './style.scss'

// Rank 1 = purple, Rank 2 = indigo, Rank 3 = blue, rest = grey
const RANK_COLORS = ['#7C3AED', '#4F46E5', '#2563EB']
const OTHER_COLOR = '#64748B'
const SUBJECT_COLOR = '#F36F46'
const RADIUS_COLOR = '#128292'

function computeMatchScore(inv) {
  const acq12 = inv.acquisitions?.totalAcquisitionCountLast12Months || 0
  const holdCount = inv.currentHoldings?.totalPropertyCount || 0
  const cashCount = inv.currentHoldings?.cashPurchasePropertyCount || 0
  const financedCount = inv.currentHoldings?.financedPropertyCount || 0
  const total = cashCount + financedCount
  const cashPct = total > 0 ? (cashCount / total) * 100 : 0
  const avgOwnership = inv.currentHoldings?.avgOwnershipLengthMonths || 0
  const disp12 = inv.dispositions?.totalDispositionCountLast12Months || 0
  let strategy = 'Investor'
  if (avgOwnership > 0 && avgOwnership < 18 && disp12 > 0) strategy = 'Fix & Flip'
  else if (avgOwnership >= 36) strategy = 'Buy & Hold'
  else if (holdCount > 5 && acq12 >= 2) strategy = 'Portfolio Builder'
  return Math.min(100, Math.round(
    Math.min(acq12 * 5, 30) + Math.min(holdCount * 2, 20) +
    (cashPct / 100) * 20 + 15 + (strategy !== 'Investor' ? 15 : 5)
  ))
}

function fmt(n) {
  if (n == null || n === 0) return ''
  return `$${Number(n).toLocaleString()}`
}

function fmtDate(d) {
  if (!d) return ''
  return d.substring(0, 10)
}

export default function CashBuyersMap({ subjectProperty, investors, properties, radius, selectedInvestorId, checkedInvestorIds = [], onSelectInvestor }) {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef([])
  const subjectMarkerRef = useRef(null)
  const circleRef = useRef(null)
  const openInfoRef = useRef(null)

  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google) return
    if (mapInstanceRef.current) return

    const ul = window.UserLocation || { latitude: 37.0902, longitude: -95.7129, zoom: 5 }
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: ul.latitude, lng: ul.longitude },
      zoom: ul.zoom,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: false,
    })
  }, [])

  useEffect(() => {
    if (window.google) {
      initMap()
    } else {
      const check = setInterval(() => {
        if (window.google) {
          clearInterval(check)
          initMap()
        }
      }, 500)
      return () => clearInterval(check)
    }
  }, [initMap])

  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map || !window.google) return

    // Clear existing
    markersRef.current.forEach((m) => m.setMap(null))
    markersRef.current = []
    if (subjectMarkerRef.current) {
      subjectMarkerRef.current.setMap(null)
      subjectMarkerRef.current = null
    }
    if (circleRef.current) {
      circleRef.current.setMap(null)
      circleRef.current = null
    }
    if (openInfoRef.current) {
      openInfoRef.current.remove()
      openInfoRef.current = null
    }

    const bounds = new window.google.maps.LatLngBounds()

    // Build investor rank lookup based on match score (fixed, doesn't change with sort)
    const investorMap = {}
    if (investors?.length > 0) {
      const sorted = [...investors].sort((a, b) => computeMatchScore(b) - computeMatchScore(a))
      sorted.forEach((inv, idx) => {
        investorMap[inv.id] = {
          rank: idx,
          name: inv.name || 'Unknown Investor',
          holdCount: inv.currentHoldings?.totalPropertyCount || 0,
        }
      })
    }

    // Subject property pin
    if (subjectProperty?.lat && subjectProperty?.lng) {
      const pos = { lat: Number(subjectProperty.lat), lng: Number(subjectProperty.lng) }
      const subjectLabel = subjectProperty.label || subjectProperty.address || 'Subject Property'

      subjectMarkerRef.current = new window.google.maps.Marker({
        position: pos,
        map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: SUBJECT_COLOR,
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2.5,
          scale: 14,
        },
        zIndex: 1000,
        title: 'Subject Property',
      })

      subjectMarkerRef.current.addListener('click', () => {
        if (openInfoRef.current) { openInfoRef.current.remove(); openInfoRef.current = null }
        showPopup(map, pos, `
          <div style="font-weight:800;font-size:14px;color:#000;padding-right:20px">Subject Property</div>
          <div style="font-size:13px;color:#000;margin-top:4px">${subjectLabel}</div>
        `)
      })

      bounds.extend(pos)

      // Radius circle
      const radiusMiles = radius || 1
      circleRef.current = new window.google.maps.Circle({
        center: pos,
        radius: radiusMiles * 1609.34,
        map,
        fillColor: RADIUS_COLOR,
        fillOpacity: 0.06,
        strokeColor: RADIUS_COLOR,
        strokeOpacity: 0.25,
        strokeWeight: 1.5,
      })
    }

    // Comparable property pins — color by investor rank
    const hasChecked = checkedInvestorIds.length > 0
    if (properties?.length > 0) {
      properties.forEach((prop) => {
        const lat = prop.address?.latitude || prop.latitude
        const lng = prop.address?.longitude || prop.longitude
        if (!lat || !lng) return

        // Filter: when investors are checked, only show their properties (current owner only)
        if (hasChecked) {
          const propInvIds = (prop.investors || [])
            .filter((pi) => pi.isCurrentOwner)
            .map((pi) => pi.id)
          const matches = propInvIds.some((id) => checkedInvestorIds.includes(id))
          if (!matches) return
        }

        const pos = { lat: Number(lat), lng: Number(lng) }

        // Find linked investor — prefer current owner, then best rank
        const propInvestors = prop.investors || []
        let matchedInvestor = null
        let rank = -1
        for (const pi of propInvestors) {
          if (!pi.isCurrentOwner) continue
          const inv = investorMap[pi.id]
          if (inv && (matchedInvestor === null || inv.rank < rank)) {
            matchedInvestor = inv
            rank = inv.rank
          }
        }
        // Fallback: if no current owner matched, try any investor
        if (!matchedInvestor) {
          for (const pi of propInvestors) {
            const inv = investorMap[pi.id]
            if (inv && (matchedInvestor === null || inv.rank < rank)) {
              matchedInvestor = inv
              rank = inv.rank
            }
          }
        }
        const color = rank >= 0 && rank < RANK_COLORS.length ? RANK_COLORS[rank] : OTHER_COLOR

        const marker = new window.google.maps.Marker({
          position: pos,
          map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
            scale: 11,
          },
          zIndex: 500 - rank,
          title: prop.address?.street || 'Property',
        })

        // Build popup data
        const investorName = matchedInvestor?.name || prop.owner?.fullName || 'Unknown'
        const holdCount = matchedInvestor?.holdCount || ''
        const address = prop.address?.street || ''
        const city = prop.address?.city || ''
        const state = prop.address?.state || ''
        const zip = prop.address?.zip || ''
        const fullAddr = [address, [city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(', ')
        const price = prop.lastSale?.price
        const saleDate = prop.lastSale?.saleDate
        const estValue = prop.assessment?.totalMarketValue
        const beds = prop.building?.bedroomCount
        const baths = prop.building?.calculatedBathroomCount || prop.building?.bathroomCount
        const sqft = prop.building?.livingAreaSquareFeet
        const yearBuilt = prop.building?.yearBuilt

        let detailParts = []
        if (beds) detailParts.push(`${beds} bd`)
        if (baths) detailParts.push(`${baths} ba`)
        if (sqft) detailParts.push(`${Number(sqft).toLocaleString()} sqft`)
        if (yearBuilt) detailParts.push(`Built ${yearBuilt}`)
        const detailsLine = detailParts.length > 0 ? `<div style="color:#888;font-size:11px;margin-top:4px">${detailParts.join(' &middot; ')}</div>` : ''

        marker.addListener('click', () => {
          if (openInfoRef.current) { openInfoRef.current.remove(); openInfoRef.current = null }
          showPopup(map, pos, `
            <div style="font-weight:800;font-size:14px;color:#000;padding-right:20px">${investorName}</div>
            ${holdCount ? `<div style="color:#22c55e;font-size:12px;font-weight:600">Owned Properties: ${holdCount}</div>` : ''}
            <div style="font-size:13px;color:#000;margin-top:6px">${fullAddr}</div>
            ${saleDate ? `<div style="color:#666;font-size:12px;margin-top:4px">Purchased: ${fmtDate(saleDate)}</div>` : ''}
            ${price ? `<div style="color:#666;font-size:12px">Price: ${fmt(price)}</div>` : ''}
            ${estValue ? `<div style="color:#666;font-size:12px">Est. Value: ${fmt(estValue)}</div>` : ''}
            ${detailsLine}
          `)
        })

        markersRef.current.push(marker)
        bounds.extend(pos)
      })
    }

    function showPopup(map, pos, html) {
      const popup = document.createElement('div')
      popup.style.cssText = 'position:absolute;z-index:999;background:#fff;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,.18);padding:12px 14px;font-family:Inter,system-ui,sans-serif;width:230px;line-height:1.4;transform:translate(-50%,-105%);'
      const close = document.createElement('div')
      close.style.cssText = 'position:absolute;top:6px;right:10px;cursor:pointer;font-size:18px;color:#aaa;line-height:1'
      close.innerHTML = '&times;'
      close.onclick = () => { overlay.setMap(null); openInfoRef.current = null }
      popup.innerHTML = html
      popup.appendChild(close)

      const overlay = new window.google.maps.OverlayView()
      overlay.onAdd = function () { this.getPanes().floatPane.appendChild(popup) }
      overlay.draw = function () {
        const pt = this.getProjection().fromLatLngToDivPixel(new window.google.maps.LatLng(pos.lat, pos.lng))
        popup.style.left = pt.x + 'px'
        popup.style.top = pt.y + 'px'
      }
      overlay.onRemove = function () { popup.remove() }
      overlay.setMap(map)
      openInfoRef.current = { remove: () => overlay.setMap(null) }
    }

    // Fit bounds
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 50 })
      const listener = map.addListener('idle', () => {
        if (map.getZoom() > 15) map.setZoom(15)
        window.google.maps.event.removeListener(listener)
      })
    }
  }, [subjectProperty, investors, properties, radius, checkedInvestorIds])

  return (
    <div className={css.mapContainer}>
      <div ref={mapRef} className={css.map} />

      {investors?.length > 0 && (
        <div className={css.mapLegend}>
          <div className={css.legendTitle}>Map Legend</div>
          <div className={css.legendItem}>
            <span className={css.legendDot} style={{ background: SUBJECT_COLOR }} />
            Subject Property
          </div>
          {RANK_COLORS.map((color, i) => (
            <div key={i} className={css.legendItem}>
              <span className={css.legendDot} style={{ background: color }} />
              Rank #{i + 1} Purchases
            </div>
          ))}
          <div className={css.legendItem}>
            <span className={css.legendDot} style={{ background: OTHER_COLOR }} />
            Other Investors
          </div>
        </div>
      )}
    </div>
  )
}
